import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, itemVariants } from '../Admin/dashboardMotion';
import { Link } from 'react-router-dom';
import { database, auth, signOut } from '../firebaseConfig';
import { ref, get, set, remove, onValue, serverTimestamp } from 'firebase/database';
import { createEmptyReport, validateReport, normalizeReport, slugify, slugWithId, genSlugSuffix, genResearchPassword, FIELD_DOCS, GENERATION_RULES } from './reportSchema';
import { buildCustomerObject, computeAutoChecklist, detectPresentationDate, DEFAULT_SUPER_STATUS } from '../Admin/structureConfig';
import { sampleReport } from './reportData';
import './ResearchAdmin.css';

const REPORTS_PATH = 'salesgod-research/reports';
const SECRETS_PATH = 'salesgod-research/reportSecrets';
const CUSTOMERS_PATH = 'salesgod-research/customers';
const PAYMENTS_PATH = 'salesgod-research/payments';
// Aufruf-Zaehler pro Report (serverseitig hochgezaehlt) und die Admin-"Beobachtung"
// (Baseline + aktiv), damit wir ein gruenes Haekchen zeigen, sobald ein neuer Aufruf kommt.
const VIEWS_PATH = 'salesgod-research/reportViews';
const WATCH_PATH = 'salesgod-research/reportWatch';
const GENERATE_FN_URL =
  process.env.REACT_APP_RESEARCH_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/generateResearchReport';
// Passwoerter liegen im client-UNLESBAREN Pfad reportSecrets. Sie werden ausschliesslich
// ueber diese admin-authentifizierte Function geladen (Bearer-Token, serverseitig geprueft).
const PASSWORDS_FN_URL =
  process.env.REACT_APP_RESEARCH_PASSWORDS_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/getResearchPasswords';
const pretty = (obj) => JSON.stringify(obj, null, 2);

// Generierungs-Schritte für die Fortschrittsanzeige (kein echtes Streaming, aber
// eine ehrliche, zeitbasierte Etappen-Anzeige, damit man sieht, dass alles läuft).
const AI_STEPS = {
  locked: [
    'Website & Angaben auswerten',
    'Signal-basierte Ziel-Unternehmen recherchieren',
    'Kontaktdaten über Google & Impressum verifizieren',
    'Potenzial bewerten & Ansprache-Strategie ableiten',
    'Mail-Konzepte auf die ersten Kontakte zuschneiden',
    'Preview speichern & Link erstellen',
  ],
  full: [
    'Website & Angaben auswerten',
    'Zielgruppen, Angles & Report generieren',
    'Angebots-, Kanal- & Kunden-Psychologie-Analyse',
    'Cold-Mails schreiben & von der KI bewerten',
    'Feinschliff & Schema-Validierung',
    'Report speichern & Link erstellen',
  ],
};
const fmtElapsed = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

const fmtDate = (ts) => {
  if (!ts || typeof ts !== 'number') return '–';
  try {
    return new Date(ts).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return '–';
  }
};

// Quelle eines Reports: "api" = über die API generiert, sonst im Dashboard erstellt.
const sourceKind = (src) => (src === 'api' ? 'api' : src ? 'dash' : 'unknown');
const sourceLabel = (src) => {
  if (src === 'api') return 'API';
  if (src === 'dashboard-ki') return 'KI';
  if (src === 'dashboard' || src === 'manuell') return 'Manuell';
  return '–';
};

const ResearchAdmin = ({ embedded = false }) => {
  const [reports, setReports] = useState([]); // [{slug, companyName, createdAt, updatedAt, createdBy}]
  const [views, setViews] = useState({}); // { slug: count } – reine Aufruf-Zaehler
  const [watch, setWatch] = useState({}); // { slug: { baseline, active, armedAt } }
  const [loadingList, setLoadingList] = useState(true);
  const [selectedSlug, setSelectedSlug] = useState(null); // null = neuer Report
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  // Stabile Zufalls-ID für den Auto-Slug eines neuen Reports (ändert sich nicht
  // bei jedem Tastendruck, sondern nur pro neuem Report).
  const [slugId, setSlugId] = useState(genSlugSuffix);
  const [jsonText, setJsonText] = useState(pretty(createEmptyReport()));
  const [feedback, setFeedback] = useState(null); // {type, errors[], warnings[], message}
  const [saving, setSaving] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  // JSON-/Felder-Editor ist standardmaessig eingeklappt. Der Normalfall ist:
  // Info-Dump rein -> generieren & Kontakt anlegen. Die Rohdaten braucht man nur
  // zum Nachjustieren, daher liegen sie hinter einem Toggle.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [createdAtOfSelected, setCreatedAtOfSelected] = useState(null);
  const [sourceOfSelected, setSourceOfSelected] = useState(null);
  const [passwordOfSelected, setPasswordOfSelected] = useState(null);
  const [passwords, setPasswords] = useState({}); // { slug: passwort } – nur via Backend-Function geladen
  const [pwLoadError, setPwLoadError] = useState(null);
  const [pwLoading, setPwLoading] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  // KI-Generierung aus Info-Dump
  const [infoDump, setInfoDump] = useState('');
  const [aiCompany, setAiCompany] = useState('');
  // Website des Unternehmens: wird der KI mitgegeben, damit sie die Seite aktiv
  // recherchieren (web_search) und Infos daraus ziehen kann.
  const [aiWebsite, setAiWebsite] = useState('');
  // Generierungs-Modus: 'full' = komplette, sofort sichtbare Research (bisheriges
  // Verhalten). 'locked' = Research-Preview: Seite wird komplett generiert, aber
  // beim Kunden ist nur der Analyse-Tab freigeschaltet, alles andere gesperrt.
  const [aiMode, setAiMode] = useState('full');
  // Research-Preview: Zielgruppen-/Fokus-Beschreibung für die Kontaktrecherche und
  // gewünschte Anzahl der Kontakte (10–20), die die KI signal-basiert suchen soll.
  const [aiTargetFocus, setAiTargetFocus] = useState('');
  const [aiContactCount, setAiContactCount] = useState(15);
  const [aiBusy, setAiBusy] = useState(false);
  // Fortschrittsanzeige der Generierung: aktueller Schritt + Laufzeit (Sekunden).
  const [aiPhase, setAiPhase] = useState(0);
  const [aiElapsed, setAiElapsed] = useState(0);
  const aiTimerRef = useRef(null);
  // Aus dem Dump extrahierte Ansprechpartner (kommen NICHT in den Report, sondern
  // landen beim Speichern am Pipeline-Kundenobjekt). Im Entwurf-Workflow halten
  // wir sie zwischen, bis manuell gespeichert wird.
  const [aiContacts, setAiContacts] = useState([]);

  // Liste live aus Firebase
  useEffect(() => {
    const r = ref(database, REPORTS_PATH);
    const unsub = onValue(
      r,
      (snap) => {
        const val = snap.val() || {};
        // Defensiv: JEDEN Schluessel im reports-Knoten anzeigen, egal wie der
        // Eintrag aussieht (auch teilweise/ungewoehnliche API-Eintraege). Ein
        // einzelner kaputter Eintrag darf nie die ganze Liste verschlucken.
        const list = Object.keys(val).map((k) => {
          const e = (val[k] && typeof val[k] === 'object') ? val[k] : {};
          let name = e.companyName;
          if (!name && typeof e.json === 'string') {
            try { name = JSON.parse(e.json)?.company?.name; } catch (_) { /* ignore */ }
          }
          return {
            slug: k,
            companyName: name || '(ohne Name)',
            createdAt: typeof e.createdAt === 'number' ? e.createdAt : null,
            updatedAt: typeof e.updatedAt === 'number' ? e.updatedAt : null,
            createdBy: e.createdBy || '—',
            source: e.source || null,
          };
        });
        list.sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0));
        setReports(list);
        setLoadingList(false);
      },
      (err) => {
        console.error('Reports-Liste konnte nicht geladen werden:', err);
        setLoadingList(false);
      });
    return () => unsub();
  }, []);

  // Aufruf-Zaehler + Beobachtungs-Status live aus Firebase (nur Admin lesbar).
  useEffect(() => {
    const rv = ref(database, VIEWS_PATH);
    const unsubV = onValue(rv, (snap) => {
      const val = snap.val() || {};
      const map = {};
      Object.keys(val).forEach((k) => { map[k] = Number(val[k] && val[k].count) || 0; });
      setViews(map);
    }, () => { /* Zaehler ist optional */ });
    const rw = ref(database, WATCH_PATH);
    const unsubW = onValue(rw, (snap) => { setWatch(snap.val() || {}); }, () => { /* optional */ });
    return () => { unsubV(); unsubW(); };
  }, []);

  // Beobachtung fuer einen Report an-/ausschalten. Beim Aktivieren merken wir uns
  // den aktuellen Zaehlerstand als Baseline; sobald ein Aufruf dazukommt, zeigt die
  // Liste ein gruenes Haekchen. Erneuter Klick deaktiviert (z. B. wenn erledigt).
  const toggleWatch = async (slug) => {
    const w = watch[slug];
    try {
      if (w && w.active) {
        await remove(ref(database, `${WATCH_PATH}/${slug}`));
      } else {
        await set(ref(database, `${WATCH_PATH}/${slug}`), {
          baseline: Number(views[slug]) || 0,
          active: true,
          armedAt: Date.now(),
        });
      }
    } catch (e) { /* ignore */ }
  };

  // Passwoerter werden NICHT clientseitig aus der DB gelesen (datenschutzkonform),
  // sondern ausschliesslich ueber die admin-authentifizierte Backend-Function.
  const loadPasswords = useCallback(async () => {
    setPwLoading(true);
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken(true) : null;
      if (!token) { setPwLoadError('Nicht eingeloggt – bitte neu anmelden.'); setPwLoading(false); return; }
      const resp = await fetch(PASSWORDS_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: '{}',
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setPwLoadError(data.error || `Passwörter konnten nicht geladen werden (HTTP ${resp.status}).`);
        setPwLoading(false);
        return;
      }
      setPwLoadError(null);
      setPasswords(data.passwords || {});
    } catch (e) {
      setPwLoadError('Passwörter konnten nicht geladen werden: ' + e.message);
    } finally {
      setPwLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (auth.currentUser) loadPasswords();
    const unsub = auth.onAuthStateChanged((u) => { if (u && !cancelled) loadPasswords(); });
    return () => { cancelled = true; unsub(); };
  }, [loadPasswords]);

  const copyToClipboard = async (text, key) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      if (key) { setCopiedKey(key); setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500); }
    } catch (e) { /* ignore */ }
  };

  // Slug automatisch aus Firmenname ableiten, solange nicht manuell editiert (nur bei neuem Report)
  const parsedCompanyName = useMemo(() => {
    try {
      return JSON.parse(jsonText)?.company?.name || '';
    } catch (e) {
      return '';
    }
  }, [jsonText]);

  useEffect(() => {
    if (selectedSlug === null && !slugTouched) {
      setSlug(slugWithId(parsedCompanyName, slugId));
    }
  }, [parsedCompanyName, slugTouched, selectedSlug, slugId]);

  const resetFeedback = () => setFeedback(null);

  const startNew = useCallback(() => {
    setSelectedSlug(null);
    setSlug('');
    setSlugTouched(false);
    setSlugId(genSlugSuffix());
    setCreatedAtOfSelected(null);
    setSourceOfSelected(null);
    setPasswordOfSelected(null);
    setJsonText(pretty(createEmptyReport()));
    setShowAdvanced(false);
    resetFeedback();
  }, []);

  const loadSample = () => {
    setJsonText(pretty(sampleReport));
    if (selectedSlug === null) setSlugTouched(false);
    resetFeedback();
  };

  const editReport = async (s) => {
    resetFeedback();
    try {
      const snap = await get(ref(database, `${REPORTS_PATH}/${s}`));
      if (!snap.exists()) {
        setFeedback({ type: 'error', message: 'Report existiert nicht mehr.' });
        return;
      }
      const entry = snap.val();
      const parsed = typeof entry.json === 'string' ? JSON.parse(entry.json) : entry.json;
      setSelectedSlug(s);
      setSlug(s);
      setSlugTouched(true);
    setCreatedAtOfSelected(entry.createdAt || null);
    setSourceOfSelected(entry.source || null);
    setPasswordOfSelected(passwords[s] || null);
    setJsonText(pretty(parsed));
    // Beim Bearbeiten eines bestehenden Reports den Felder-Editor direkt aufklappen.
    setShowAdvanced(true);
    } catch (e) {
      setFeedback({ type: 'error', message: `Konnte Report nicht laden: ${e.message}` });
    }
  };

  const deleteReport = async (s) => {
    if (!window.confirm(`Report „${s}" wirklich löschen? Der Link wird ungültig und der Kontakt verschwindet auch aus der Sales Pipeline.`)) return;
    try {
      await remove(ref(database, `${REPORTS_PATH}/${s}`));
      try { await remove(ref(database, `${SECRETS_PATH}/${s}`)); } catch (e) { /* Secret evtl. nicht vorhanden */ }
      // Sales-Pipeline-Kontakt mitloeschen, damit keine verwaisten Eintraege zurueckbleiben.
      try { await remove(ref(database, `${CUSTOMERS_PATH}/${s}`)); } catch (e) { /* Pipeline-Kontakt evtl. nicht vorhanden */ }
      // Daran haengende Zahlungen (paymentId = "<slug>__<periodKey>") ebenfalls entfernen.
      try {
        const paySnap = await get(ref(database, PAYMENTS_PATH));
        const payments = paySnap.val() || {};
        await Promise.all(
          Object.keys(payments)
            .filter((id) => payments[id]?.customerSlug === s || id.startsWith(`${s}__`))
            .map((id) => remove(ref(database, `${PAYMENTS_PATH}/${id}`)))
        );
      } catch (e) { /* Zahlungen evtl. nicht vorhanden */ }
      setPasswords((prev) => { const next = { ...prev }; delete next[s]; return next; });
      if (selectedSlug === s) startNew();
    } catch (e) {
      setFeedback({ type: 'error', message: `Löschen fehlgeschlagen: ${e.message}` });
    }
  };

  const formatJson = () => {
    try {
      setJsonText(pretty(JSON.parse(jsonText)));
      resetFeedback();
    } catch (e) {
      setFeedback({ type: 'error', message: `JSON ungültig: ${e.message}` });
    }
  };

  const validateOnly = () => {
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e) {
      setFeedback({ type: 'error', message: `JSON ungültig: ${e.message}` });
      return null;
    }
    const result = validateReport(parsed);
    setFeedback({ type: result.valid ? 'ok' : 'error', errors: result.errors, warnings: result.warnings, message: result.valid ? 'Schema gültig.' : 'Schema-Fehler gefunden.' });
    return result.valid ? parsed : null;
  };

  // Fortschritt starten: zeitbasierte Etappen-Anzeige. Die letzten zwei Schritte
  // bleiben bewusst offen, bis der Request wirklich fertig ist (kein falsches „100%").
  const startAiProgress = useCallback((modeForSteps) => {
    const steps = AI_STEPS[modeForSteps] || AI_STEPS.full;
    const perStep = modeForSteps === 'locked' ? 9 : 16; // grobe Schätzung je Schritt (s)
    setAiPhase(0);
    setAiElapsed(0);
    if (aiTimerRef.current) clearInterval(aiTimerRef.current);
    const t0 = Date.now();
    aiTimerRef.current = setInterval(() => {
      const s = Math.floor((Date.now() - t0) / 1000);
      setAiElapsed(s);
      const cap = Math.max(0, steps.length - 2);
      setAiPhase(Math.min(cap, Math.floor(s / perStep)));
    }, 1000);
  }, []);
  const stopAiProgress = useCallback(() => {
    if (aiTimerRef.current) { clearInterval(aiTimerRef.current); aiTimerRef.current = null; }
  }, []);
  // Timer immer stoppen, sobald die Generierung endet (Erfolg oder Fehler).
  useEffect(() => { if (!aiBusy) stopAiProgress(); }, [aiBusy, stopAiProgress]);
  useEffect(() => () => { if (aiTimerRef.current) clearInterval(aiTimerRef.current); }, []);

  // Generieren & direkt den Kontakt anlegen (Standard-Workflow). Die Function
  // speichert serverseitig (save:true): Report, Zugangs-Passwort, Pipeline-Kontakt
  // und erkennt einen evtl. Präsentationstermin aus dem Info-Dump.
  const generateAndCreate = async () => {
    if (aiBusy) return;
    // Im Research-Preview reicht eine Website (die KI recherchiert daraus); sonst
    // ist der Info-Dump Pflicht.
    const lockedWithSite = aiMode === 'locked' && (aiWebsite || '').trim().length > 3;
    if (infoDump.trim().length < 20 && !lockedWithSite) {
      setFeedback({ type: 'error', message: aiMode === 'locked' ? 'Bitte eine Website angeben oder einen Info-Dump (min. 20 Zeichen) liefern.' : 'Info-Dump ist zu kurz (min. 20 Zeichen). Bitte mehr Kontext zum Unternehmen liefern.' });
      return;
    }
    setAiBusy(true);
    startAiProgress(aiMode);
    resetFeedback();
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      const resp = await fetch(GENERATE_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ infoDump, companyName: aiCompany || undefined, website: aiWebsite || undefined, mode: aiMode, ...(aiMode === 'locked' ? { targetFocus: aiTargetFocus || undefined, contactCount: aiContactCount } : {}), save: true }),
      });
      let data = {};
      try { data = await resp.json(); } catch (e) { /* nicht-JSON Antwort */ }
      if (!resp.ok) {
        // 422 o.ä.: Entwurf trotzdem in den Editor laden, Felder aufklappen, damit
        // man die Schema-Hinweise direkt beheben und manuell speichern kann.
        if (data.report) {
          setJsonText(pretty(data.report));
          setShowAdvanced(true);
          if (selectedSlug === null && data.slug) { setSlug(data.slug); setSlugTouched(false); }
        }
        setFeedback({
          type: 'error',
          message: data.error || `Generierung fehlgeschlagen (HTTP ${resp.status}).`,
          errors: data.validation && data.validation.errors,
          warnings: data.validation && data.validation.warnings,
        });
        setAiBusy(false);
        return;
      }
      if (data.report && data.slug) {
        setSelectedSlug(data.slug);
        setSlug(data.slug);
        setSlugTouched(true);
        setSourceOfSelected(data.source || 'dashboard-ki');
        setPasswordOfSelected(data.password || null);
        if (data.password) setPasswords((prev) => ({ ...prev, [data.slug]: data.password }));
        setJsonText(pretty(data.report));
        const v = data.validation || { valid: true, errors: [], warnings: [] };
        // Research-Preview (Locked): Der volle Report wird bewusst NICHT erzeugt
        // (nur der Analyse-Tab), daher sind Full-Report-Hinweise erwartbar und werden
        // hier unterdrueckt – der Rest entsteht beim Entsperren aus einem 2. Info-Dump.
        const isLocked = data.mode === 'locked';
        setFeedback({
          type: 'ok',
          message: `Kontakt angelegt: ${data.report.company?.name || data.slug}${isLocked ? ' · Research-Preview (nur Analyse-Tab frei, Rest folgt beim Entsperren)' : ''}${data.password ? ` · Passwort: ${data.password}` : ''}`,
          savedSlug: data.slug,
          warnings: isLocked ? [] : v.warnings,
        });
        setInfoDump('');
        setAiCompany('');
        setAiWebsite('');
        setAiTargetFocus('');
        // Bei "generieren & anlegen" haengt die Function die Ansprechpartner schon
        // selbst ans Kundenobjekt – lokalen Zwischenspeicher daher leeren.
        setAiContacts([]);
      } else {
        setFeedback({ type: 'error', message: 'Keine Report-Daten erhalten.' });
      }
    } catch (e) {
      setFeedback({ type: 'error', message: 'Netzwerk-/Funktionsfehler: ' + e.message + ' (Ist die Cloud Function deployed und der ANTHROPIC_API_KEY gesetzt?)' });
    }
    setAiBusy(false);
  };

  // Nur einen Entwurf erzeugen (NICHT speichern) – landet im Felder-Editor zum
  // Prüfen/Anpassen, der dann aufgeklappt wird.
  const generateDraft = async () => {
    if (aiBusy) return;
    const lockedWithSite = aiMode === 'locked' && (aiWebsite || '').trim().length > 3;
    if (infoDump.trim().length < 20 && !lockedWithSite) {
      setFeedback({ type: 'error', message: aiMode === 'locked' ? 'Bitte eine Website angeben oder einen Info-Dump (min. 20 Zeichen) liefern.' : 'Info-Dump ist zu kurz (min. 20 Zeichen). Bitte mehr Kontext zum Unternehmen liefern.' });
      return;
    }
    setAiBusy(true);
    startAiProgress(aiMode);
    resetFeedback();
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      const resp = await fetch(GENERATE_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ infoDump, companyName: aiCompany || undefined, website: aiWebsite || undefined, mode: aiMode, ...(aiMode === 'locked' ? { targetFocus: aiTargetFocus || undefined, contactCount: aiContactCount } : {}), save: false }),
      });
      let data = {};
      try { data = await resp.json(); } catch (e) { /* nicht-JSON Antwort */ }
      if (!resp.ok) {
        setFeedback({ type: 'error', message: data.error || `Generierung fehlgeschlagen (HTTP ${resp.status}).`, errors: data.validation && data.validation.errors });
        setAiBusy(false);
        return;
      }
      if (data.report) {
        setJsonText(pretty(data.report));
        setShowAdvanced(true);
        if (selectedSlug === null && data.slug) { setSlug(data.slug); setSlugTouched(false); }
        // Ansprechpartner aus dem Dump merken – beim manuellen Speichern wandern
        // sie ans Pipeline-Kundenobjekt (nicht in den Report).
        setAiContacts(Array.isArray(data.contacts) ? data.contacts : []);
        const v = data.validation || { valid: true, errors: [], warnings: [] };
        // Research-Preview (Locked): nur der Analyse-Tab wird generiert – Full-Report-
        // Schemahinweise sind erwartbar und werden hier nicht als Fehler gezeigt.
        const isLocked = data.mode === 'locked';
        setFeedback({
          type: (v.valid || isLocked) ? 'ok' : 'error',
          message: isLocked
            ? 'Research-Preview-Entwurf erstellt (nur Analyse-Tab) – bitte unten prüfen und speichern. Der Rest wird beim Entsperren erzeugt.'
            : (v.valid ? 'KI-Entwurf erstellt – bitte unten prüfen, ggf. anpassen und speichern.' : 'KI-Entwurf erstellt, aber mit Schema-Hinweisen (bitte vor dem Speichern beheben):'),
          errors: isLocked ? [] : v.errors,
          warnings: isLocked ? [] : v.warnings,
        });
      } else {
        setFeedback({ type: 'error', message: 'Keine Report-Daten erhalten.' });
      }
    } catch (e) {
      setFeedback({ type: 'error', message: 'Netzwerk-/Funktionsfehler: ' + e.message + ' (Ist die Cloud Function deployed und der ANTHROPIC_API_KEY gesetzt?)' });
    }
    setAiBusy(false);
  };

  const handleSave = async () => {
    if (saving) return;
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e) {
      setFeedback({ type: 'error', message: `JSON ungültig: ${e.message}` });
      return;
    }
    const result = validateReport(parsed);
    if (!result.valid) {
      setFeedback({ type: 'error', errors: result.errors, warnings: result.warnings, message: 'Bitte zuerst die Schema-Fehler beheben.' });
      return;
    }
    const finalSlug = (slug || slugWithId(parsed.company.name, slugId)).trim();
    if (!finalSlug) {
      setFeedback({ type: 'error', message: 'Kein gültiger Slug – bitte Firmenname oder Slug angeben.' });
      return;
    }
    // Beim Anlegen prüfen, ob der Slug schon belegt ist
    if (selectedSlug === null) {
      const existing = await get(ref(database, `${REPORTS_PATH}/${finalSlug}`));
      if (existing.exists()) {
        setFeedback({ type: 'error', message: `Slug „${finalSlug}" ist bereits vergeben. Bitte anpassen.` });
        return;
      }
    }
    // Zugangs-Passwort: bei bestehenden Reports beibehalten, bei neuen erzeugen.
    // Das Passwort wird NICHT im reports-Knoten gespeichert, sondern im separaten,
    // client-unlesbaren Secret-Pfad (Schreiben nur durch Master-Admin erlaubt).
    const finalPassword = (selectedSlug !== null && (passwords[finalSlug] || passwordOfSelected))
      ? (passwords[finalSlug] || passwordOfSelected)
      : genResearchPassword(parsed.company.name);
    setSaving(true);
    try {
      await set(ref(database, `${REPORTS_PATH}/${finalSlug}`), {
        slug: finalSlug,
        companyName: parsed.company.name,
        json: JSON.stringify(normalizeReport(parsed)),
        createdAt: selectedSlug !== null && createdAtOfSelected ? createdAtOfSelected : serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || 'unknown',
        source: selectedSlug !== null && sourceOfSelected ? sourceOfSelected : 'dashboard',
      });
      await set(ref(database, `${SECRETS_PATH}/${finalSlug}`), { password: finalPassword });
      // Operatives Kundenobjekt nur beim ERSTanlegen erzeugen (nicht beim Bearbeiten),
      // damit der im Struktur-Board gesetzte Status nicht überschrieben wird.
      if (selectedSlug === null) {
        try {
          // Checkliste anhand der Report-Daten vorausfüllen (was ist schon da?).
          const auto = computeAutoChecklist(DEFAULT_SUPER_STATUS, normalizeReport(parsed));
          // Präsentationstermin aus dem Info-Dump erkennen (falls vorhanden).
          const presentationAt = detectPresentationDate(infoDump);
          const base = buildCustomerObject({
            slug: finalSlug,
            companyName: parsed.company.name,
            createdBy: auth.currentUser?.email || 'unknown',
            source: 'dashboard',
          });
          // Ansprechpartner (aus dem Dump extrahiert) ans Pipeline-Kundenobjekt
          // haengen – kundensicher getrennt vom Report. Nur valide Eintraege.
          const contacts = (Array.isArray(aiContacts) ? aiContacts : [])
            .map((c) => ({
              name: String(c?.name || '').slice(0, 160),
              role: String(c?.role || '').slice(0, 160),
              email: String(c?.email || '').slice(0, 200),
              phone: String(c?.phone || '').slice(0, 80),
              note: String(c?.note || '').slice(0, 600),
              owner: c?.owner === true,
            }))
            .filter((c) => c.name || c.email || c.phone);
          await set(ref(database, `${CUSTOMERS_PATH}/${finalSlug}`), {
            ...base,
            checklist: auto,
            checklistAuto: auto,
            ...(contacts.length ? { contacts } : {}),
            ...(aiMode === 'locked' ? { researchMode: 'locked' } : {}),
            ...(presentationAt ? { presentationAt, presentationSource: 'auto' } : {}),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            statusEnteredAt: serverTimestamp(),
          });
        } catch (e) { console.error('Kundenobjekt konnte nicht angelegt werden:', e); }
      }
      setSelectedSlug(finalSlug);
      setSlug(finalSlug);
      setSlugTouched(true);
      setPasswordOfSelected(finalPassword);
      setPasswords((prev) => ({ ...prev, [finalSlug]: finalPassword }));
      // Zwischengespeicherte Ansprechpartner sind nun (beim Erstanlegen) persistiert.
      setAiContacts([]);
      setFeedback({ type: 'ok', warnings: result.warnings, message: `Gespeichert. Link: /research/${finalSlug} · Passwort: ${finalPassword}`, savedSlug: finalSlug });
    } catch (e) {
      setFeedback({ type: 'error', message: `Speichern fehlgeschlagen: ${e.message}` });
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = (s) => `${window.location.origin}/research/${s}`;

  return (
    <div className={`ra-wrap ${embedded ? 'ra-embedded' : ''}`}>
      {!embedded && (
        <header className="ra-top">
          <div className="ra-top-left">
            <img src="/img/sgbbc.webp" alt="SalesGod" className="ra-top-logo" />
            <span className="ra-logo">Research Admin</span>
            <span className="ra-user">{auth.currentUser?.email}</span>
          </div>
          <div className="ra-top-actions">
            <Link className="ra-btn ra-btn-ghost" to="/research" target="_blank" rel="noreferrer">Demo ansehen</Link>
            <button className="ra-btn ra-btn-ghost" onClick={() => signOut(auth)}>Abmelden</button>
          </div>
        </header>
      )}

      <motion.div className="ra-body" variants={staggerContainer} initial="initial" animate="animate">
        {/* Liste */}
        <motion.aside className="ra-list" variants={itemVariants}>
          <div className="ra-list-head">
            <span>Reports ({reports.length})</span>
            <button className="ra-btn ra-btn-primary ra-btn-sm" onClick={startNew}>+ Neu</button>
          </div>
          <div className="ra-pwbar">
            <span className="ra-pwbar-label">
              Passwörter {pwLoading ? '· lädt…' : `· ${Object.keys(passwords).length} geladen`}
            </span>
            <button className="ra-btn ra-btn-ghost ra-btn-sm" onClick={loadPasswords} disabled={pwLoading} title="Passwörter neu laden">
              {pwLoading ? '…' : '↻ neu laden'}
            </button>
          </div>
          {pwLoadError && <div className="ra-pwbar-error">{pwLoadError}</div>}
          {loadingList ? (
            <div className="ra-list-empty">Laden…</div>
          ) : reports.length === 0 ? (
            <div className="ra-list-empty">Noch keine Reports. Lege links oben einen neuen an.</div>
          ) : (
            <ul className="ra-list-items">
              {reports.map((r) => (
                <li key={r.slug} className={`ra-list-item ${selectedSlug === r.slug ? 'ra-list-item-active' : ''}`}>
                  <button className="ra-list-item-main" onClick={() => editReport(r.slug)}>
                    <span className="ra-list-item-name">
                      {r.companyName}
                      <span className={`ra-src ra-src-${sourceKind(r.source)}`}>{sourceLabel(r.source)}</span>
                    </span>
                    <span className="ra-list-item-slug">/research/{r.slug}</span>
                    {passwords[r.slug] ? (
                      <span
                        className="ra-list-item-pw"
                        role="button"
                        tabIndex={0}
                        title="Passwort kopieren"
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(passwords[r.slug], r.slug); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); copyToClipboard(passwords[r.slug], r.slug); } }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="15" r="4" /><path d="m10.85 12.15 6.65-6.65M18 8l2-2M16 6l1.5-1.5" /></svg>
                        <span className="ra-list-item-pw-val">{passwords[r.slug]}</span>
                        <span className="ra-list-item-pw-copy">{copiedKey === r.slug ? 'kopiert ✓' : 'kopieren'}</span>
                      </span>
                    ) : (
                      <span className="ra-list-item-pw ra-list-item-pw-muted">{pwLoading ? 'Passwort lädt…' : 'kein Passwort'}</span>
                    )}
                    <span className="ra-list-item-meta">{fmtDate(r.updatedAt)} · {r.createdBy}</span>
                  </button>
                  <div className="ra-list-item-tools">
                    {(() => {
                      const count = Number(views[r.slug]) || 0;
                      const w = watch[r.slug];
                      const active = !!(w && w.active);
                      const baseline = active ? (Number(w.baseline) || 0) : 0;
                      const delta = active ? Math.max(0, count - baseline) : 0;
                      const hit = active && delta > 0;
                      const watchTitle = hit
                        ? `Neuer Aufruf! Jetzt ${count} (Start ${baseline}, +${delta}). Klick = erledigt`
                        : active
                          ? `Beobachtung aktiv – warte auf neuen Aufruf (Start ${baseline}). Klick zum Deaktivieren`
                          : `Auf neuen Aufruf beobachten (aktuell ${count})`;
                      return (
                        <>
                          <span className="ra-view-chip" title={`${count} Aufruf${count === 1 ? '' : 'e'}`}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></svg>
                            <span className="ra-view-chip-num">{count}</span>
                          </span>
                          <button
                            className={`ra-icon-btn ra-watch-btn${active ? ' ra-watch-on' : ''}${hit ? ' ra-watch-hit' : ''}`}
                            onClick={(e) => { e.stopPropagation(); toggleWatch(r.slug); }}
                            title={watchTitle}
                          >
                            {hit ? (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                            ) : active ? (
                              <span className="ra-watch-dot" aria-hidden="true" />
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
                            )}
                          </button>
                        </>
                      );
                    })()}
                    <a href={publicUrl(r.slug)} target="_blank" rel="noreferrer" className="ra-icon-btn" title="Öffnen">↗</a>
                    <button className="ra-icon-btn ra-icon-del" onClick={() => deleteReport(r.slug)} title="Löschen">×</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </motion.aside>

        {/* Editor */}
        <motion.main className="ra-editor" variants={itemVariants}>
          <div className="ra-editor-head">
            <h1>{selectedSlug === null ? 'Neuer Report' : `Report bearbeiten: ${selectedSlug}`}</h1>
            <div className="ra-editor-head-actions">
              <button className="ra-btn ra-btn-ghost" onClick={loadSample} title="Demo-Report als Vorlage laden">Demo als Vorlage</button>
              <button className="ra-btn ra-btn-ghost" onClick={() => { startNew(); }} title="Leeres Template">Leeres Template</button>
            </div>
          </div>

          {/* ── KI: Info-Dump → Report ── */}
          <div className="ra-ai">
            <div className="ra-ai-head">
              <span className="ra-ai-eyebrow">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />
                </svg>
                KI-Generierung
              </span>
              <span className="ra-ai-title">Aus Info-Dump erstellen</span>
              <span className="ra-ai-hint">Rohe Notizen rein → die KI recherchiert Fakten & schätzt den Rest branchengerecht. „Generieren & Kontakt anlegen" speichert den Report direkt und legt den Kontakt in der Sales-Pipeline an.</span>
              <span className="ra-ai-note">Nur firmenbezogene Infos einfügen. Keine personenbezogenen Daten (Klarnamen, E-Mail, Telefon, Adressen einzelner Personen).</span>
            </div>

            <div className="ra-ai-fields">
              <label className="ra-ai-field">
                <span className="ra-ai-field-label">Firmenname <span className="ra-ai-field-opt">optional</span></span>
                <input
                  type="text"
                  className="ra-ai-company"
                  value={aiCompany}
                  placeholder="Sonst aus Info-Dump abgeleitet"
                  onChange={(e) => setAiCompany(e.target.value)}
                />
              </label>
              <label className="ra-ai-field">
                <span className="ra-ai-field-label">Website <span className="ra-ai-field-opt">optional</span></span>
                <input
                  type="text"
                  className="ra-ai-website"
                  value={aiWebsite}
                  placeholder="KI recherchiert die Seite aktiv"
                  onChange={(e) => setAiWebsite(e.target.value)}
                />
              </label>
            </div>

            {/* Generierungs-Modus: bestimmt, was der Kunde nach der Erstellung sieht. */}
            <div className="ra-ai-block">
              <span className="ra-ai-block-label">Modus</span>
              <div className="ra-ai-mode" role="radiogroup" aria-label="Generierungs-Modus">
                <button
                  type="button"
                  className={`ra-ai-mode-opt ${aiMode === 'full' ? 'is-active' : ''}`}
                  role="radio"
                  aria-checked={aiMode === 'full'}
                  onClick={() => setAiMode('full')}
                >
                  <span className="ra-ai-mode-radio" aria-hidden="true" />
                  <span className="ra-ai-mode-text">
                    <span className="ra-ai-mode-title">Full-Mode</span>
                    <span className="ra-ai-mode-desc">Komplette Research – alle Tabs sofort sichtbar.</span>
                  </span>
                </button>
                <button
                  type="button"
                  className={`ra-ai-mode-opt ${aiMode === 'locked' ? 'is-active' : ''}`}
                  role="radio"
                  aria-checked={aiMode === 'locked'}
                  onClick={() => setAiMode('locked')}
                >
                  <span className="ra-ai-mode-radio" aria-hidden="true" />
                  <span className="ra-ai-mode-text">
                    <span className="ra-ai-mode-title">Research-Mode (Preview)</span>
                    <span className="ra-ai-mode-desc">Nur der Analyse-Tab ist freigeschaltet – alles andere gesperrt.</span>
                  </span>
                </button>
              </div>
            </div>

            {aiMode === 'locked' && (
              <div className="ra-ai-preview">
                <div className="ra-ai-preview-head">
                  <span className="ra-ai-preview-title">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
                    </svg>
                    Signal-basierte Kontaktliste
                  </span>
                  <span className="ra-ai-preview-hint">Die KI recherchiert nach dem Erstellen echte Ziel-Unternehmen (Zielgruppe des Kunden) inkl. Geschäftsführer, Website, E-Mail, Potenzial-Rating und Strategie.</span>
                </div>
                <textarea
                  className="ra-ai-input ra-ai-focus"
                  value={aiTargetFocus}
                  placeholder="Auf welche Unternehmen/Zielgruppe sollen wir uns beziehen? z. B. „Zahnarztpraxen in Bayern mit 3–10 Mitarbeitern“ …"
                  onChange={(e) => setAiTargetFocus(e.target.value)}
                />
                <div className="ra-ai-slider">
                  <label className="ra-ai-slider-label">
                    Anzahl Kontakte
                    <span className="ra-ai-slider-value">{aiContactCount}</span>
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="20"
                    step="1"
                    value={aiContactCount}
                    onChange={(e) => setAiContactCount(Number(e.target.value))}
                  />
                  <div className="ra-ai-slider-scale"><span>10</span><span>20</span></div>
                </div>
              </div>
            )}

            <label className="ra-ai-block">
              <span className="ra-ai-block-label">
                Info-Dump {aiMode === 'locked' && <span className="ra-ai-field-opt">optional – Website genügt</span>}
              </span>
              <textarea
                className="ra-ai-dump"
                value={infoDump}
                placeholder={aiMode === 'locked'
                  ? 'Optional: Ergänzende Infos. Ist eine Website angegeben, zieht die KI die Angaben automatisch daraus.'
                  : 'Alles, was wir über das Unternehmen wissen – Branche, Angebot, Zielgruppe, Deal-Größe, Marge, Website, Besonderheiten, Region, Präsentationstermin …'}
                onChange={(e) => setInfoDump(e.target.value)}
              />
            </label>
            <div className="ra-ai-actions">
              <button className="ra-btn ra-btn-primary" onClick={generateAndCreate} disabled={aiBusy}>
                {aiBusy && <span className="ra-btn-spinner" aria-hidden="true" />}
                {aiBusy ? 'KI arbeitet…' : 'Generieren & Kontakt anlegen'}
              </button>
              <button className="ra-btn ra-btn-ghost" onClick={generateDraft} disabled={aiBusy} title="Nur einen Entwurf erzeugen, noch nichts speichern">
                Nur Entwurf erzeugen
              </button>
              {infoDump && <button className="ra-btn ra-btn-ghost" onClick={() => setInfoDump('')} disabled={aiBusy}>Leeren</button>}
            </div>

            {aiBusy && (() => {
              const steps = AI_STEPS[aiMode] || AI_STEPS.full;
              return (
                <div className="ra-ai-progress" role="status" aria-live="polite">
                  <div className="ra-ai-progress-head">
                    <span className="ra-ai-spinner" aria-hidden="true" />
                    <div className="ra-ai-progress-head-txt">
                      <span className="ra-ai-progress-title">
                        {aiMode === 'locked' ? 'Research-Preview wird erstellt' : 'Komplette Research wird generiert'}
                      </span>
                      <span className="ra-ai-progress-sub">
                        Schritt {Math.min(aiPhase + 1, steps.length)} von {steps.length} · läuft seit {fmtElapsed(aiElapsed)}
                      </span>
                    </div>
                  </div>
                  <ul className="ra-ai-steps">
                    {steps.map((label, i) => {
                      const state = i < aiPhase ? 'done' : i === aiPhase ? 'active' : 'pending';
                      return (
                        <li key={i} className={`ra-ai-step ra-ai-step-${state}`}>
                          <span className="ra-ai-step-ic" aria-hidden="true">{state === 'done' ? '✓' : ''}</span>
                          <span className="ra-ai-step-label">{label}</span>
                        </li>
                      );
                    })}
                  </ul>
                  <p className="ra-ai-progress-note">
                    {aiMode === 'locked'
                      ? 'Die KI recherchiert echte Ziel-Unternehmen und verifiziert die Kontakte über Google & Impressum. Dauert meist unter 1 Minute – bitte das Fenster geöffnet lassen.'
                      : 'Das kann 1 bis 3 Minuten dauern (Recherche, Angebots-/Kanal-Analyse, Cold-Mails, Feinschliff). Bitte das Fenster geöffnet lassen.'}
                  </p>
                </div>
              );
            })()}
          </div>

          {feedback && (
            <div className={`ra-feedback ra-feedback-${feedback.type}`}>
              {feedback.message && <p className="ra-feedback-msg">{feedback.message}</p>}
              {feedback.savedSlug && (
                <a className="ra-feedback-link" href={publicUrl(feedback.savedSlug)} target="_blank" rel="noreferrer">
                  {publicUrl(feedback.savedSlug)} ↗
                </a>
              )}
              {feedback.errors && feedback.errors.length > 0 && (
                <ul className="ra-feedback-errors">{feedback.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
              )}
              {feedback.warnings && feedback.warnings.length > 0 && (
                <ul className="ra-feedback-warnings">{feedback.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}</ul>
              )}
            </div>
          )}

          {/* ── Erweitert: Rohdaten (Slug, Passwort, JSON) hinter Toggle ── */}
          <div className="ra-adv">
            <button className="ra-adv-toggle" onClick={() => setShowAdvanced((v) => !v)}>
              {showAdvanced ? '▾' : '▸'} Erweitert: Felder & JSON manuell bearbeiten
              {selectedSlug !== null && <span className="ra-adv-badge">{selectedSlug}</span>}
            </button>
            {showAdvanced && (
              <div className="ra-adv-body">
          <div className="ra-field">
            <label>URL-Slug</label>
            <div className="ra-slug-row">
              <span className="ra-slug-prefix">/research/</span>
              <input
                type="text"
                value={slug}
                disabled={selectedSlug !== null}
                onChange={(e) => { setSlug(slugify(e.target.value)); setSlugTouched(true); }}
                placeholder="wird-aus-firmenname-erzeugt"
              />
            </div>
            {selectedSlug !== null && <span className="ra-hint">Slug ist bei bestehenden Reports fixiert (sonst würde der Link brechen).</span>}
          </div>

          <div className="ra-field">
            <label>Zugangs-Passwort</label>
            <div className="ra-slug-row">
              <input
                type="text"
                readOnly
                value={passwordOfSelected || ''}
                placeholder={selectedSlug === null ? 'wird beim Speichern erzeugt (Firmenname + Zahl)' : '—'}
              />
              {passwordOfSelected && (
                <button
                  type="button"
                  className="ra-btn ra-btn-ghost ra-btn-sm"
                  onClick={() => copyToClipboard(passwordOfSelected, '__editor__')}
                  title="Passwort kopieren"
                >{copiedKey === '__editor__' ? 'Kopiert ✓' : 'Kopieren'}</button>
              )}
            </div>
            <span className="ra-hint">Dieses Passwort muss eingegeben werden, um die Research-Seite zu öffnen. Wird serverseitig geprüft und nur dem Master-Admin angezeigt.</span>
            {pwLoadError && <span className="ra-hint" style={{ color: '#f87171' }}>{pwLoadError}</span>}
          </div>

          <div className="ra-field ra-field-grow">
            <label>Report-JSON</label>
            <textarea
              className="ra-json"
              value={jsonText}
              spellCheck={false}
              onChange={(e) => { setJsonText(e.target.value); resetFeedback(); }}
            />
          </div>

          <div className="ra-actions">
            <button className="ra-btn ra-btn-ghost" onClick={formatJson}>JSON formatieren</button>
            <button className="ra-btn ra-btn-ghost" onClick={validateOnly}>Validieren</button>
            <button className="ra-btn ra-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Speichert…' : selectedSlug === null ? 'Manuell anlegen & speichern' : 'Änderungen speichern'}
            </button>
          </div>
              </div>
            )}
          </div>

          {/* Schema-Referenz */}
          <div className="ra-docs">
            <button className="ra-docs-toggle" onClick={() => setShowDocs((v) => !v)}>
              {showDocs ? '▾' : '▸'} Schema-Dokumentation (Felder, Format, Länge, Stil)
            </button>
            {showDocs && (
              <div className="ra-docs-body">
                <p className="ra-docs-intro">
                  Jede Research-Seite wird vollständig aus diesem JSON gerendert. Geld-Zahlen (Preise, ROI, Szenarien)
                  werden NICHT gespeichert, sondern live aus <code>config</code> berechnet. Diese Tabelle ist auch die
                  Spezifikation für eine spätere API/KI-Generierung.
                </p>
                <div className="ra-docs-rules">
                  <h3>Schreibregeln (wie die KI formulieren muss)</h3>
                  <ol>
                    {GENERATION_RULES.map((rule, i) => <li key={i}>{rule}</li>)}
                  </ol>
                </div>
                {FIELD_DOCS.map((section) => (
                  <div key={section.group} className="ra-docs-section">
                    <h3>{section.group}</h3>
                    <table className="ra-docs-table">
                      <thead>
                        <tr><th>Feld</th><th>Typ</th><th>Pflicht</th><th>Länge/Range</th><th>Stil / Beschreibung</th></tr>
                      </thead>
                      <tbody>
                        {section.fields.map((f) => (
                          <tr key={f.path}>
                            <td><code>{f.path}</code></td>
                            <td>{f.type}</td>
                            <td>{f.required ? 'ja' : '–'}</td>
                            <td>{f.range || (f.max ? `≤ ${f.max} Z.` : '')}{f.count ? ` · ${f.count}` : ''}</td>
                            <td>{f.style}{f.example ? <><br /><span className="ra-docs-ex">z. B. {f.example}</span></> : null}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.main>
      </motion.div>
    </div>
  );
};

export default ResearchAdmin;
